import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ref, onValue, update, remove, get } from "firebase/database";
import { db as database } from "./firebase/config";
import "./AdminUserManagement.css";

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showUserModal, setShowUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: "",
    end: "",
  });
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // State để lưu thông tin mã giới thiệu và ngân hàng của user được chọn
  const [userPromotionHistory, setUserPromotionHistory] = useState([]);
  const [userBankInfo, setUserBankInfo] = useState(null);

  // Toast notification function
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // Sort functionality
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Filter and search logic
  const applyFiltersAndSearch = (usersList, search, status, dateRange) => {
    let filtered = usersList;

    // Search filter
    if (search) {
      filtered = filtered.filter(
        (user) =>
          (user.profile?.displayName || user.name || "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (user.profile?.email || user.email || "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (user.phone || "").includes(search) ||
          (user.id || "").toLowerCase().includes(search.toLowerCase())
      );
    }

    // Status filter
    if (status !== "all") {
      filtered = filtered.filter((user) => {
        const now = new Date();
        const hasValidMembership =
          user.membership &&
          user.membershipExpiry &&
          new Date(user.membershipExpiry) > now;

        switch (status) {
          case "premium":
            return hasValidMembership;
          case "free":
            return !hasValidMembership;
          case "expired":
            return (
              user.membership &&
              user.membershipExpiry &&
              new Date(user.membershipExpiry) <= now
            );
          default:
            return true;
        }
      });
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((user) => {
        if (!user.createdAt) return false;
        const userDate = new Date(user.createdAt);
        const startDate = dateRange.start ? new Date(dateRange.start) : null;
        const endDate = dateRange.end ? new Date(dateRange.end) : null;

        if (startDate && userDate < startDate) return false;
        if (endDate && userDate > endDate) return false;
        return true;
      });
    }

    return filtered;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersRef = ref(database, "users");

        onValue(
          usersRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const usersData = snapshot.val();
              const usersArray = Object.keys(usersData).map((key) => ({
                id: key,
                ...usersData[key],
              }));

              // Sort users by creation date (newest first)
              usersArray.sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB - dateA;
              });

              setUsers(usersArray);
              const filtered = applyFiltersAndSearch(
                usersArray,
                searchTerm,
                filterStatus,
                selectedDateRange
              );
              setFilteredUsers(filtered);
            } else {
              setUsers([]);
              setFilteredUsers([]);
            }
            setLoading(false);
          },
          (error) => {
            console.error("Error fetching users:", error);
            showToast(
              "Lỗi khi tải dữ liệu người dùng: " + error.message,
              "error"
            );
            setLoading(false);
          }
        );
      } catch (error) {
        console.error("Error in fetchUsers:", error);
        showToast("Lỗi khi tải dữ liệu: " + error.message, "error");
        setLoading(false);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters when search term, filter status, or date range changes
  useEffect(() => {
    const filtered = applyFiltersAndSearch(
      users,
      searchTerm,
      filterStatus,
      selectedDateRange
    );
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [users, searchTerm, filterStatus, selectedDateRange]);

  // Apply sorting
  useEffect(() => {
    if (sortConfig.key) {
      const sorted = [...filteredUsers].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key === "name") {
          aValue = a.profile?.displayName || a.name || "";
          bValue = b.profile?.displayName || b.name || "";
        } else if (sortConfig.key === "email") {
          aValue = a.profile?.email || a.email || "";
          bValue = b.profile?.email || b.email || "";
        }

        // Handle dates
        if (
          sortConfig.key === "createdAt" ||
          sortConfig.key === "membershipExpiry"
        ) {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
      setFilteredUsers(sorted);
    }
  }, [sortConfig, filteredUsers]);

  const handleUserClick = (user) => {
    setCurrentUser(user);
    setShowUserModal(true);
    setEditMode(false);

    // Load thông tin mã giới thiệu và ngân hàng
    loadUserAdditionalInfo(user.id);
  };

  // Hàm load thông tin bổ sung của user (promotion codes và bank info)
  const loadUserAdditionalInfo = async (userId) => {
    try {
      // Load promotion code history
      const promotionCodeRef = ref(database, `users/${userId}/promotionCode`);
      const promotionSnapshot = await get(promotionCodeRef);
      if (promotionSnapshot.exists()) {
        const promotionData = promotionSnapshot.val();
        const promotionArray = Object.values(promotionData).sort(
          (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
        );
        setUserPromotionHistory(promotionArray);
      } else {
        setUserPromotionHistory([]);
      }

      // Load bank info
      const bankInfoRef = ref(database, `users/${userId}/bankInfo`);
      const bankSnapshot = await get(bankInfoRef);
      if (bankSnapshot.exists()) {
        setUserBankInfo(bankSnapshot.val());
      } else {
        setUserBankInfo(null);
      }
    } catch (error) {
      console.error("Error loading user additional info:", error);
      setUserPromotionHistory([]);
      setUserBankInfo(null);
    }
  };

  const handleEditUser = () => {
    setEditMode(true);
  };

  const handleSaveUser = async () => {
    try {
      if (!currentUser.id) {
        showToast("Không tìm thấy ID người dùng", "error");
        return;
      }

      const userRef = ref(database, `users/${currentUser.id}`);

      // Prepare update data
      const updateData = {
        phone: currentUser.phone || "",
        membership: currentUser.membership || null,
        membershipExpiry: currentUser.membershipExpiry || null,
      };

      // Update profile data if it exists
      if (currentUser.profile) {
        updateData.profile = {
          ...currentUser.profile,
          displayName: currentUser.profile.displayName || "",
          email: currentUser.profile.email || "",
        };
      }

      await update(userRef, updateData);

      showToast("Cập nhật thông tin người dùng thành công!");
      setEditMode(false);

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === currentUser.id ? { ...user, ...updateData } : user
        )
      );
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Lỗi khi cập nhật thông tin: " + error.message, "error");
    }
  };

  const handleDeleteUser = async () => {
    if (window.confirm("Bạn có chắc chắn muốn xóa người dùng này không?")) {
      try {
        const userRef = ref(database, `users/${currentUser.id}`);
        await remove(userRef);

        showToast("Xóa người dùng thành công!");
        setShowUserModal(false);

        // Update local state
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== currentUser.id)
        );
      } catch (error) {
        console.error("Error deleting user:", error);
        showToast("Lỗi khi xóa người dùng: " + error.message, "error");
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setSelectedDateRange({ start: "", end: "" });
    setSortConfig({ key: null, direction: "asc" });
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Hiển thị {indexOfFirstUser + 1}-
          {Math.min(indexOfLastUser, filteredUsers.length)} của{" "}
          {filteredUsers.length} người dùng
        </div>
        <div className="pagination-buttons">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Trước
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="pagination-button"
              >
                1
              </button>
              {startPage > 2 && <span className="pagination-dots">...</span>}
            </>
          )}

          {pageNumbers.map((number) => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`pagination-button ${
                currentPage === number ? "active" : ""
              }`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="pagination-dots">...</span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="pagination-button"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Sau
          </button>
        </div>
      </div>
    );
  };

  const renderMembershipStatus = (user) => {
    const now = new Date();
    const hasValidMembership =
      user.membership &&
      user.membershipExpiry &&
      new Date(user.membershipExpiry) > now;

    if (hasValidMembership) {
      return <span className="status-badge success">Premium</span>;
    } else if (
      user.membership &&
      user.membershipExpiry &&
      new Date(user.membershipExpiry) <= now
    ) {
      return <span className="status-badge warning">Hết hạn</span>;
    } else {
      return <span className="status-badge inactive">Free</span>;
    }
  };

  // Statistics calculations
  const totalUsers = users.length;
  const premiumUsers = users.filter((user) => {
    const now = new Date();
    return (
      user.membership &&
      user.membershipExpiry &&
      new Date(user.membershipExpiry) > now
    );
  }).length;
  const freeUsers = users.filter((user) => {
    const now = new Date();
    return (
      !user.membership ||
      !user.membershipExpiry ||
      new Date(user.membershipExpiry) <= now
    );
  }).length;
  const expiredUsers = users.filter((user) => {
    const now = new Date();
    return (
      user.membership &&
      user.membershipExpiry &&
      new Date(user.membershipExpiry) <= now
    );
  }).length;

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="skeleton-container">
      {[...Array(5)].map((_, index) => (
        <tr key={index} className="skeleton-row">
          <td className="skeleton skeleton-user"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-badge"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
        </tr>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              <h3>Đang tải dữ liệu...</h3>
              <p>Vui lòng chờ trong giây lát</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === "success" ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="content-header">
        <div>
          <h2>Quản lý người dùng</h2>
          <p>Quản lý và theo dõi thông tin người dùng trong hệ thống</p>
        </div>
        <button
          onClick={() => navigate("/admin-dashboard")}
          className="btn btn-primary"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Quay lại Dashboard
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card users-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Tổng người dùng</p>
              <p className="stat-card-value">{totalUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="stat-card active-users-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Premium</p>
              <p className="stat-card-value">{premiumUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="stat-card transactions-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Free</p>
              <p className="stat-card-value">{freeUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="stat-card revenue-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Hết hạn</p>
              <p className="stat-card-value">{expiredUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="data-table">
        <div className="table-header">
          <div className="table-title">
            <h3>Danh sách người dùng</h3>
          </div>
          <div className="table-actions">
            <div className="search-box">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên, email, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
              />
              <svg
                className="search-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-select"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="premium">Premium</option>
              <option value="free">Free</option>
              <option value="expired">Hết hạn</option>
            </select>
            <input
              type="date"
              value={selectedDateRange.start}
              onChange={(e) =>
                setSelectedDateRange((prev) => ({
                  ...prev,
                  start: e.target.value,
                }))
              }
              className="form-input"
            />
            <input
              type="date"
              value={selectedDateRange.end}
              onChange={(e) =>
                setSelectedDateRange((prev) => ({
                  ...prev,
                  end: e.target.value,
                }))
              }
              className="form-input"
            />
            {(searchTerm ||
              filterStatus !== "all" ||
              selectedDateRange.start ||
              selectedDateRange.end) && (
              <button onClick={clearAllFilters} className="btn btn-secondary">
                Xóa bộ lọc
              </button>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div className="table-content">
          <table className="modern-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("name")}>
                  Người dùng {getSortIndicator("name")}
                </th>
                <th onClick={() => handleSort("email")}>
                  Email {getSortIndicator("email")}
                </th>
                <th>Trạng thái</th>
                <th onClick={() => handleSort("membershipExpiry")}>
                  Hết hạn {getSortIndicator("membershipExpiry")}
                </th>
                <th onClick={() => handleSort("createdAt")}>
                  Ngày tạo {getSortIndicator("createdAt")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonLoader />
              ) : currentUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-state">
                    <div className="empty-icon">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                    </div>
                    <p>Không tìm thấy người dùng</p>
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => handleUserClick(user)}
                    className="table-row-hover"
                  >
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {(
                            user.profile?.displayName ||
                            user.name ||
                            user.id ||
                            "U"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="user-details">
                          <p className="user-name">
                            {user.profile?.displayName ||
                              user.name ||
                              "Chưa có tên"}
                          </p>
                          <p className="user-id">
                            ID: {user.id.substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="user-email">
                      {user.profile?.email || user.email || "Chưa có email"}
                    </td>
                    <td>{renderMembershipStatus(user)}</td>
                    <td className="user-date">
                      {user.membershipExpiry ? (
                        <span
                          className={
                            new Date(user.membershipExpiry) <= new Date()
                              ? "text-error"
                              : "text-success"
                          }
                        >
                          {new Date(user.membershipExpiry).toLocaleDateString(
                            "vi-VN"
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">Không có</span>
                      )}
                    </td>
                    <td className="user-date">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("vi-VN")
                        : "Không xác định"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>

      {/* User Detail Modal */}
      {showUserModal && currentUser && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-title">
                <div className="user-avatar">
                  {(
                    currentUser.profile?.displayName ||
                    currentUser.name ||
                    currentUser.id ||
                    "U"
                  )
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div>
                  <h3>
                    {editMode
                      ? "Chỉnh sửa thông tin người dùng"
                      : "Chi tiết người dùng"}
                  </h3>
                  <p>
                    {currentUser.profile?.displayName ||
                      currentUser.name ||
                      "Người dùng"}
                  </p>
                </div>
              </div>
              <div className="modal-close">
                {renderMembershipStatus(currentUser)}
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setUserPromotionHistory([]);
                    setUserBankInfo(null);
                  }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="text"
                    value={currentUser.id || ""}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Tên</label>
                  <input
                    type="text"
                    name="name"
                    value={
                      currentUser.profile?.displayName || currentUser.name || ""
                    }
                    onChange={(e) => {
                      const updatedUser = { ...currentUser };
                      if (!updatedUser.profile) updatedUser.profile = {};
                      updatedUser.profile.displayName = e.target.value;
                      setCurrentUser(updatedUser);
                    }}
                    disabled={!editMode}
                    className={`form-input ${!editMode ? "disabled" : ""}`}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={
                      currentUser.profile?.email || currentUser.email || ""
                    }
                    onChange={(e) => {
                      const updatedUser = { ...currentUser };
                      if (!updatedUser.profile) updatedUser.profile = {};
                      updatedUser.profile.email = e.target.value;
                      setCurrentUser(updatedUser);
                    }}
                    disabled={!editMode}
                    className={`form-input ${!editMode ? "disabled" : ""}`}
                  />
                </div>
                <div className="form-group">
                  <label>Điện thoại</label>
                  <input
                    type="text"
                    name="phone"
                    value={currentUser.phone || ""}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    className={`form-input ${!editMode ? "disabled" : ""}`}
                  />
                </div>
                <div className="form-group">
                  <label>Loại membership</label>
                  {editMode ? (
                    <select
                      name="membership"
                      value={
                        currentUser.membership !== undefined &&
                        currentUser.membership !== null
                          ? currentUser.membership.toString()
                          : ""
                      }
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Free</option>
                      <option value="1">Gói 7 ngày</option>
                      <option value="2">Gói 30 ngày</option>
                      <option value="3">Gói 180 ngày</option>
                      <option value="4">Gói 360 ngày</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={(() => {
                        if (!currentUser.membership) return "Free";
                        const membershipId =
                          typeof currentUser.membership === "string"
                            ? parseInt(currentUser.membership)
                            : currentUser.membership;

                        switch (membershipId) {
                          case 1:
                            return "Gói 7 ngày";
                          case 2:
                            return "Gói 30 ngày";
                          case 3:
                            return "Gói 180 ngày";
                          case 4:
                            return "Gói 360 ngày";
                          default:
                            return `Premium (${currentUser.membership})`;
                        }
                      })()}
                      disabled
                      className="form-input disabled"
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Ngày hết hạn membership</label>
                  <input
                    type="datetime-local"
                    name="membershipExpiry"
                    value={(() => {
                      if (!currentUser.membershipExpiry) return "";
                      try {
                        const expiryDate = new Date(
                          currentUser.membershipExpiry
                        );
                        if (isNaN(expiryDate.getTime())) return "";
                        return expiryDate.toISOString().slice(0, 16);
                      } catch (error) {
                        console.error("Lỗi xử lý membershipExpiry:", error);
                        return "";
                      }
                    })()}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    className={`form-input ${!editMode ? "disabled" : ""}`}
                  />
                </div>
                <div className="form-group">
                  <label>Ngày tạo</label>
                  <input
                    type="text"
                    value={(() => {
                      if (!currentUser.createdAt) return "";
                      try {
                        const createdDate = new Date(currentUser.createdAt);
                        if (isNaN(createdDate.getTime()))
                          return "Không xác định";
                        return createdDate.toLocaleString("vi-VN");
                      } catch (error) {
                        console.error("Lỗi xử lý createdAt:", error);
                        return "Không xác định";
                      }
                    })()}
                    disabled
                    className="form-input disabled"
                  />
                </div>
              </div>

              {/* Thông tin mã giới thiệu */}
              <div className="additional-info-section">
                <h5>Lịch sử mã giới thiệu</h5>
                {userPromotionHistory.length > 0 ? (
                  <div className="table-content">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>Mã giới thiệu</th>
                          <th>Ngày nhập</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userPromotionHistory.map((entry, index) => (
                          <tr key={index}>
                            <td className="promotion-code">{entry.code}</td>
                            <td className="promotion-date">
                              {new Date(entry.createdAt).toLocaleDateString(
                                "vi-VN",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state-small">
                    <p>Chưa có mã giới thiệu nào</p>
                  </div>
                )}
              </div>

              {/* Thông tin tài khoản ngân hàng */}
              <div className="additional-info-section">
                <h5>Thông tin tài khoản ngân hàng</h5>
                {userBankInfo ? (
                  <div className="bank-info-grid">
                    <div className="bank-info-item">
                      <label>Tên ngân hàng:</label>
                      <span>{userBankInfo.bankName}</span>
                    </div>
                    <div className="bank-info-item">
                      <label>Tên chủ tài khoản:</label>
                      <span>{userBankInfo.accountHolderName}</span>
                    </div>
                    <div className="bank-info-item">
                      <label>Số tài khoản:</label>
                      <span>{userBankInfo.accountNumber}</span>
                    </div>
                    <div className="bank-info-item">
                      <label>Cập nhật lần cuối:</label>
                      <span>
                        {new Date(userBankInfo.updatedAt).toLocaleDateString(
                          "vi-VN",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="empty-state-small">
                    <p>Chưa có thông tin tài khoản ngân hàng</p>
                  </div>
                )}
              </div>

              {currentUser.fanpages && (
                <div className="fanpages-section">
                  <h5>Fanpages đã kết nối</h5>
                  <div className="table-content">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>ID Fanpage</th>
                          <th>Tên Fanpage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(currentUser.fanpages).map(
                          ([id, data]) => (
                            <tr key={id}>
                              <td className="fanpage-id">{id}</td>
                              <td className="fanpage-name">{data.name}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="user-id-display">ID: {currentUser.id}</div>
              <div className="modal-actions">
                {editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(false)}
                      className="btn btn-secondary"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={handleSaveUser}
                      className="btn btn-primary"
                    >
                      Lưu thay đổi
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleDeleteUser}
                      className="btn btn-danger"
                    >
                      Xóa người dùng
                    </button>
                    <button
                      onClick={handleEditUser}
                      className="btn btn-success"
                    >
                      Chỉnh sửa
                    </button>
                    <button
                      onClick={() => {
                        setShowUserModal(false);
                        setUserPromotionHistory([]);
                        setUserBankInfo(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Đóng
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
